import React, { type ReactNode, type ComponentType, useEffect } from 'react';
import ProjectPagesComponent from '@atlassiansox/project-pages';
import { EmbeddedProductUpdater } from '@atlassian/jira-analytics-providers/src/common/utils/embedded-product';
import { ProjectBreadcrumb } from '@atlassian/jira-breadcrumbs';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { Redirect } from '@atlassian/jira-router';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller';
import { pagesFeatureStates, usePagesFeatureState } from '../common/state/pages-feature-store';
import messages from './messages';
import type { ProjectPagesTheme } from './types';
import { onFireErrorEvent } from './utils';

type Props = {
	isAdmin: boolean;
	// eslint-disable-next-line jira/deprecations/no-base-url
	baseUrl: string;
	locale: Locale;
	cloudId: string;
	accountId: string | undefined | null;
	projectKey: string;
	projectId: string | number;
	projectName: string;
	projectType: string;
	environment: string | undefined | null;
	theme?: ProjectPagesTheme;
	breadcrumbs: ReactNode;
	PerformanceTracking?: ComponentType<{
		loading: boolean;
	}>;
	parentProduct?: string;
};

// Project Pages Simplified Landing feature flag evaluation (https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jsw_project_pages_simplified_landing_page_exp/setup #team-starburst)
// eslint-disable-next-line jira/ff/inline-usage
const evaluateSimplifiedLandingCohort = () =>
	expVal('jsw_project_pages_simplified_landing_page_exp', 'cohort', 'not-enrolled');

const getFeatureFlagValue = () => ({
	// example:
	// isGrapeOn, <- imported from 'project-pages/feature-flags'
});
const onFireExposureEvent = () => ({});

const ProjectPagesApp = (props: Props) => {
	const licensedProducts = useLicensedProducts();
	const hasConfluence = !!licensedProducts[LICENSED_PRODUCTS.CONFLUENCE];
	const { formatMessage } = useIntl();

	const featureState = usePagesFeatureState(props.projectId);

	const isJWMProduct = props.parentProduct && props.parentProduct === 'JWM';

	const simplifiedLandingCohort =
		isJWMProduct || hasConfluence ? 'not-enrolled' : evaluateSimplifiedLandingCohort();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		const event = createAnalyticsEvent({});
		fireTrackAnalytics(event, 'projectPagesSimplifiedLanding exposed', {
			cohort: simplifiedLandingCohort,
		});
	}, [createAnalyticsEvent, simplifiedLandingCohort]);

	/*
        Adding not a JWM product check is a hacky solution for now to solve
        https://jira.atlassian.com/browse/JWMCLOUD-319.

        Long term solution would be to investigate further if we need to add check project-pages-link-enabled property for JWM as well,
        if no then we need to pull out the redirection check from src/packages/confluence/project-pages package to src/packages/spa-apps/project/pages
        so that the change is only limited to JSW and not flowing to other consumers of project pages (like JWM)
    */
	if (featureState === pagesFeatureStates.DISABLED && !isJWMProduct) {
		return <Redirect to={`/projects/${props.projectKey}`} />;
	}

	const { PerformanceTracking } = props;
	const getProjectPagesComponent = () => {
		const projectPagesComponent = (
			<ProjectPagesComponent
				{...props}
				// @ts-expect-error - TS2322 - Type 'ReactNode' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
				breadcrumbs={!ff('sea-2744-jsw-tabs-nav') && props.breadcrumbs}
				EmbeddedProductUpdater={EmbeddedProductUpdater}
				onGetFeatureFlagValue={getFeatureFlagValue}
				onFireExposureEvent={onFireExposureEvent}
				onFireErrorEvent={onFireErrorEvent}
				hasConfluence={hasConfluence}
				renderFullHeader={!ff('sea-2744-jsw-tabs-nav')}
				simplifiedLandingCohort={simplifiedLandingCohort}
				isProjectPagesSporkRebrandingEnabled
			/>
		);
		return projectPagesComponent;
	};

	return (
		<ReportErrors id="projectPages" packageName="jiraProjectPages" attributes={{ hasConfluence }}>
			<>
				{ff('sea-2744-jsw-tabs-nav') ? (
					<>
						{getProjectPagesComponent()}
						{PerformanceTracking ? (
							<PerformanceTracking loading={featureState === pagesFeatureStates.LOADING} />
						) : null}
					</>
				) : (
					<>
						<DocumentTitle title={formatMessage(messages.pageTitlePages)} />
						<ProjectBreadcrumb
							baseUrl={props.baseUrl}
							projectKey={props.projectKey}
							projectName={props.projectName}
						>
							{getProjectPagesComponent()}
							{PerformanceTracking ? (
								<PerformanceTracking loading={featureState === pagesFeatureStates.LOADING} />
							) : null}
						</ProjectBreadcrumb>
					</>
				)}
			</>
		</ReportErrors>
	);
};

export default ProjectPagesApp;
